// Old path — a BODIED delegate, and the one place the Work composer still names
// Chat. The submenu primitives now live at
// `packages/agents/src/ui/projects/components/LinkContextButton.parts.tsx`
// and are published through the single `.` door; what stays here is the binding
// of the one input they could not take with them.
//
// WHY THE BINDING IS AT THIS PATH AND NOT AT THE COMPOSER THAT RENDERS THE MENU.
// Chat is quarantined and the couplings are shrink-only IN BOTH DIRECTIONS, at
// importer→target grain. This file already holds the
// `-> source-control-v2/source-control-popover-v2` pair, so keeping the import
// here moves nothing: the set neither grows nor shrinks. Binding it one level up,
// at `ProjectsLandingComposer`, would have retired one pair and minted another —
// the same coupling, re-addressed, and a gate failure for no product change.
//
// The panel itself cannot cross: it reads the source-control atoms, the Evidence
// billing tier, the team enforcement rules and four feature flags. Re-homing it
// out of Chat would not have helped either — the census scores any `@/` specifier,
// so the submenu would still be naming an app module. A render prop is what frees
// it, and what the package keeps is the part that is actually the Work menu's: the
// Sources row, the hover grace, and the pointer watch that closes the flyout.
//
// THE FILE IS A COMPONENTS-ONLY MODULE, and both halves of that are deliberate.
// The four submenu primitives are not re-exported: nothing app-side reads them —
// `LinkContextButton` is their only consumer and it lives in the package now — so
// re-exporting them would publish dead surface on a locked root. And the binding
// below is a PascalCase COMPONENT rather than a lower-camel render function,
// because `react-refresh/only-export-components` is ratcheted at zero and a module
// mixing the two trips it. The seam is typed `SourcesPopoverComponent` for that
// reason and for a React one — the panel gets its own hook scope.
//
// WHY NOT A SEPARATE `*.sources-binding.tsx` MODULE, which is the other way to
// unmix a module: the Chat pair is pinned to THIS PATH by the shrink-only ratchet.
// Moving the import to any new file retires one pair and mints another, and the
// ratchet refuses the mint — so a split that reads tidier costs a quarantine
// exception, and this one costs nothing.
import { SourceControlPopoverV2 } from '@/components/chat/components/source-control-v2/source-control-popover-v2';
import {
  isEvidenceApiAvailable,
  isEvidenceWebSearchAvailable,
} from '@/utils/backendSurfaces';
import type { SourcesPopoverComponent } from '@heidi/agents';

/**
 * The Evidence source-control panel, bound for the Work composer's Sources row.
 *
 * `side`/`align` are supplied here rather than inside the package because where
 * the flyout sits relative to the add-menu is the composing surface's decision —
 * it opens to the right, as the second panel, which is a fact about this menu's
 * layout and not about the panel.
 *
 * The row and the panel are withheld together when the build serves neither
 * surface the panel selects between — the same two terms `SourcesButton` gates
 * the chat composer's control on. The row is this binding's child, so returning
 * no element drops it rather than leaving a row that opens nothing.
 */
export const WorkSourcesPopover: SourcesPopoverComponent = ({
  open,
  onOpenChange,
  children,
}) =>
  isEvidenceWebSearchAvailable || isEvidenceApiAvailable ? (
    <SourceControlPopoverV2
      side="right"
      align="start"
      open={open}
      onOpenChange={onOpenChange}
      suppressOpenAutoFocus
    >
      {children}
    </SourceControlPopoverV2>
  ) : (
    <></>
  );
